import { conversationLanguages } from 'lib-shared-languages';
import { AsyncStatus } from 'lib-shared-types-api';
import { useCallback, useMemo } from 'react';
import { useSetState } from 'react-use';

import { aiApi, CreateAiTextTranslationApiArg } from 'apis/ai';

const initialResult: UseTranslateTextResponse = {
  asyncStatus: 'uninitialized',
  translatedText: undefined,
  originalArg: undefined,
  error: undefined,
  feedbackId: undefined,
};

/** Marker the model returns in the body when it could not translate. */
export const translationFailText = 'FAILED_TO_TRANSLATE';

type UseTranslateTextResult = [
  /** The translate trigger function. */
  initiateFn: TranslateFn,
  /** The response of the translation process. */
  response: UseTranslateTextResponse,
];

/**
 * A hook for translating text to a given language.
 *
 * @remarks
 *   Because the translation happens through LLM, it is not necessary to provide
 *   the source language code.
 * @example
 *   const [translate] = useTranslateText();
 *
 *   // In some function
 *   try {
 *     const response = await translate({
 *       language: 'fr',
 *       text: 'Hello world!',
 *     });
 *     // do something with `response.translatedText`
 *   } catch (error) {
 *     // handle error
 *   }
 *
 * @example
 *   const [translate, { asyncStatus, translatedText, error }] =
 *     useTranslateText();
 *
 *   // In some function
 *   translate({
 *     language: 'fr',
 *     text: 'Hello world!',
 *   }).catch((error) => console.error(error));
 *
 *   // meanwhile in a hook
 *   useEffect(() => {
 *     if (asyncStatus === 'rejected') {
 *       // handle error
 *     }
 *   }, [asyncStatus]);
 */
export function useTranslateText(): UseTranslateTextResult {
  const [response, setResponse] =
    useSetState<UseTranslateTextResponse>(initialResult);
  const [createTranslation] = aiApi.useCreateAiTextTranslationMutation();

  const initiate = useCallback<TranslateFn>(
    async (arg) => {
      setResponse(initialResult);

      const languageName =
        conversationLanguages.find((lng) => arg.language?.startsWith(lng.code))
          ?.name || arg.language;

      try {
        setResponse({ asyncStatus: 'pending' });

        const createTranslationResponse = await createTranslation({
          text: arg.text,
          language: languageName,
          csid: arg.csid,
          contentType: arg.contentType,
          direction: arg.direction,
        }).unwrap();

        const { text, feedback_id: feedbackId } = createTranslationResponse;

        if (text.includes(translationFailText)) {
          const error = 'Translation was not successful';

          setResponse({
            asyncStatus: 'rejected',
            error,
            originalArg: arg,
          });

          return Promise.reject(error);
        }

        setResponse({
          asyncStatus: 'fulfilled',
          translatedText: text,
          originalArg: arg,
          feedbackId,
        });

        return Promise.resolve({
          translatedText: text,
        });
      } catch (error) {
        setResponse({ asyncStatus: 'rejected', error, originalArg: arg });

        return Promise.reject(error);
      }
    },
    [createTranslation, setResponse],
  );

  return useMemo(() => [initiate, response], [initiate, response]);
}

export type UseTranslateTextResponse = {
  /** The async status of the translation process. */
  readonly asyncStatus: AsyncStatus;
  /** The arg provided to the initiate function. */
  readonly originalArg?: CreateAiTextTranslationApiArg;
  /** The translated text. */
  readonly translatedText?: string;
  /** The translation error. */
  readonly error?: unknown;
  /**
   * The feedback id from an endpoint that produced or contains AI data, e.g.
   * conversation summary, translation.
   */
  readonly feedbackId?: string;
  /** Direction */
  readonly direction?: 'inbound' | 'outbound';
};

type TranslateFn = (
  arg: CreateAiTextTranslationApiArg,
) => Promise<Pick<UseTranslateTextResponse, 'translatedText'>>;
